import { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { Sentry } from "@/lib/sentry";
import { CIEDA_PHONE, CIEDA_PHONE_TEL } from "@/lib/constants";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Application error:", error, errorInfo);
    // Report to Sentry (no-op if DSN not configured)
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack ?? "" } },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-6">
          <div className="text-center max-w-md">
            <p className="text-7xl font-bold font-display text-secondary mb-4">500</p>
            <h1 className="text-2xl font-bold text-foreground mb-3">
              Une erreur est survenue
            </h1>
            <p className="text-muted-foreground mb-6">
              Nous sommes désolés, quelque chose s'est mal passé.
              Contactez-nous au{" "}
              <a href={CIEDA_PHONE_TEL} className="text-secondary hover:underline">
                {CIEDA_PHONE}
              </a>{" "}
              si le problème persiste.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 rounded-xl bg-secondary text-secondary-foreground font-semibold hover:opacity-90 transition-opacity"
            >
              Rafraîchir la page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
